import React, { Component, useEffect } from 'react';
import { array, bool, func, oneOf, object, shape, string, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { createResourceLocatorString } from '../../util/routes';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  getQueryParamNames,
  isOriginInUse,
} from '../../util/search';
import { parse, redirectToStore, stringify } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { H3, H5, Page, ItemSourceToggle } from '../../components';
import BrandCarousel from '../../components/BrandCarousel/BrandCarousel';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  sanitizeTaxonomyFilterParams,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import { mergeWithStoreSidebarFilters } from '../../components/StoreSidebar/StoreSidebar';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;
const EMBEDDED_FILTER_DEBOUNCE_MS = 300;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      embeddedKeywordValue: validUrlQueryParamsFromProps(props).keywords || '',
      showFilters: true,
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);
    this.toggleFilters = this.toggleFilters.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
    this.handleChannelChange = this.handleChannelChange.bind(this);
    this.handleBrandSelect = this.handleBrandSelect.bind(this);
    this.handleEmbeddedKeywordChange = this.handleEmbeddedKeywordChange.bind(this);
    this.handleEmbeddedKeywordSubmit = this.handleEmbeddedKeywordSubmit.bind(this);

    this.embeddedQueryUpdateTimeout = null;
  }

  componentWillUnmount() {
    if (this.embeddedQueryUpdateTimeout) {
      clearTimeout(this.embeddedQueryUpdateTimeout);
      this.embeddedQueryUpdateTimeout = null;
    }
  }

  componentDidUpdate(prevProps) {
    const prevKeywords = validUrlQueryParamsFromProps(prevProps).keywords || '';
    const nextKeywords = validUrlQueryParamsFromProps(this.props).keywords || '';

    if (prevKeywords !== nextKeywords && nextKeywords !== this.state.embeddedKeywordValue) {
      this.setState({ embeddedKeywordValue: nextKeywords });
    }
  }

  toggleFilters() {
    this.setState(prevState => ({ showFilters: !prevState.showFilters }));
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const {
      history,
      routeConfiguration,
      config,
      pageName = 'SearchPage',
      pagePathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {}, embeddedKeywordValue: '' });

    if (this.embeddedQueryUpdateTimeout) {
      clearTimeout(this.embeddedQueryUpdateTimeout);
      this.embeddedQueryUpdateTimeout = null;
    }

    // Reset routing params
    const queryParams = omit(urlQueryParams, [
      ...filterQueryParamNames,
      'keywords',
      'pub_deliveryOption',
      'pub_condition',
      'channel',
    ]);
    history.push(
      createResourceLocatorString(pageName, routeConfiguration, pagePathParams, queryParams)
    );
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      pageName = 'SearchPage',
      pagePathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords, author_id } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        const authorIdMaybe = author_id ? { author_id } : {};
        return {
          currentQueryParams: {
            ...mergedQueryParams,
            ...updatedURLParams,
            ...keywordsMaybe,
            ...authorIdMaybe,
            address,
            bounds,
          },
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(
            searchParams,
            listingFieldsConfig,
            defaultFiltersConfig,
            sortConfig
          );
          const nextLocation = createResourceLocatorString(
            pageName,
            routeConfiguration,
            pagePathParams,
            search
          );

          if (this.props.isEmbedded) {
            if (this.embeddedQueryUpdateTimeout) {
              clearTimeout(this.embeddedQueryUpdateTimeout);
            }

            this.embeddedQueryUpdateTimeout = setTimeout(() => {
              history.push(nextLocation);
              this.embeddedQueryUpdateTimeout = null;
            }, EMBEDDED_FILTER_DEBOUNCE_MS);
          } else {
            history.push(nextLocation);
          }
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const {
      history,
      routeConfiguration,
      pageName = 'SearchPage',
      pagePathParams = {},
    } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    if (this.embeddedQueryUpdateTimeout) {
      clearTimeout(this.embeddedQueryUpdateTimeout);
      this.embeddedQueryUpdateTimeout = null;
    }

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(
      createResourceLocatorString(pageName, routeConfiguration, pagePathParams, queryParams)
    );
  }

  handleChannelChange(nextChannel) {
    const {
      history,
      routeConfiguration,
      config,
      pageName = 'SearchPage',
      pagePathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    if (this.embeddedQueryUpdateTimeout) {
      clearTimeout(this.embeddedQueryUpdateTimeout);
      this.embeddedQueryUpdateTimeout = null;
    }

    const mergedQueryParams = { ...urlQueryParams, ...this.state.currentQueryParams };
    const nextQueryParams =
      nextChannel === 'warehouse'
        ? { ...mergedQueryParams, channel: 'warehouse' }
        : omit(mergedQueryParams, 'channel');
    const search = cleanSearchFromConflictingParams(
      nextQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig
    );

    this.setState(
      prevState => ({
        currentQueryParams:
          nextChannel === 'warehouse'
            ? { ...prevState.currentQueryParams, channel: 'warehouse' }
            : omit(prevState.currentQueryParams, 'channel'),
      }),
      () => {
        history.push(
          createResourceLocatorString(pageName, routeConfiguration, pagePathParams, search)
        );
      }
    );
  }

  handleBrandSelect(nextBrand) {
    const {
      history,
      routeConfiguration,
      config,
      pageName = 'SearchPage',
      pagePathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    if (this.embeddedQueryUpdateTimeout) {
      clearTimeout(this.embeddedQueryUpdateTimeout);
      this.embeddedQueryUpdateTimeout = null;
    }

    const mergedQueryParams = { ...urlQueryParams, ...this.state.currentQueryParams };
    const nextQueryParams = nextBrand
      ? { ...mergedQueryParams, pub_brand: nextBrand }
      : omit(mergedQueryParams, 'pub_brand');
    const search = cleanSearchFromConflictingParams(
      nextQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig
    );

    this.setState(
      prevState => ({
        currentQueryParams: nextBrand
          ? { ...prevState.currentQueryParams, pub_brand: nextBrand }
          : omit(prevState.currentQueryParams, 'pub_brand'),
      }),
      () => {
        history.push(
          createResourceLocatorString(pageName, routeConfiguration, pagePathParams, search)
        );
      }
    );
  }

  handleEmbeddedKeywordChange(event) {
    this.setState({ embeddedKeywordValue: event.target.value });
  }

  handleEmbeddedKeywordSubmit(event) {
    event.preventDefault();

    const {
      history,
      routeConfiguration,
      config,
      pageName = 'SearchPage',
      pagePathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const trimmedKeyword = this.state.embeddedKeywordValue.trim();
    const mergedQueryParams = { ...urlQueryParams, ...this.state.currentQueryParams };
    const nextQueryParams = trimmedKeyword
      ? { ...mergedQueryParams, keywords: trimmedKeyword }
      : omit(mergedQueryParams, 'keywords');
    const search = cleanSearchFromConflictingParams(
      nextQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig
    );

    if (this.embeddedQueryUpdateTimeout) {
      clearTimeout(this.embeddedQueryUpdateTimeout);
      this.embeddedQueryUpdateTimeout = null;
    }

    this.setState(
      prevState => ({
        currentQueryParams: trimmedKeyword
          ? { ...prevState.currentQueryParams, keywords: trimmedKeyword }
          : omit(prevState.currentQueryParams, 'keywords'),
      }),
      () => {
        history.push(
          createResourceLocatorString(pageName, routeConfiguration, pagePathParams, search)
        );
      }
    );
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings,
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      routeConfiguration,
      config,
      embeddedTitle,
    } = this.props;

    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;
    const { hideHeaderAndFooter, isEmbedded } = this.props;

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig,
      isOriginInUse(config)
    );

    const validQueryParams = validFilterParams(
      searchParamsInURL,
      listingFieldsConfig,
      defaultFiltersConfig,
      false
    );

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const defaultFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => f.key !== 'keywords')
      : defaultFiltersConfig;
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availableFilters = [
      ...customPrimaryFilters,
      ...defaultFilters,
      ...customSecondaryFilters,
    ];
    const currentCategory = validQueryParams.pub_category || null;
    const filtersToRender = isEmbedded
      ? mergeWithStoreSidebarFilters(availableFilters, currentCategory)
      : availableFilters;

    // Selected aka active filters
    const selectedFilters = validFilterParams(
      validQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig
    );
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig
    );
    const sortBy = (mode, extraProps = {}) => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
          {...extraProps}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
      />
    );

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    const { showFilters, embeddedKeywordValue } = this.state;
    const currentChannel = urlQueryParams.channel === 'warehouse' ? 'warehouse' : 'in-store';
    const showChannelToggle = !!this.props.showSourceToggle;
    const currentBrand = parse(location.search).pub_brand || null;
    const showBrandCarousel =
      !!this.props.showBrandCarousel && Array.isArray(this.props.brands) && this.props.brands.length > 0;

    const content = (
      <div
        className={classNames(css.layoutWrapperContainer, {
          [css.layoutWrapperContainerEmbedded]: isEmbedded,
        })}
      >
        {showFilters && (
          <aside
            className={classNames(css.layoutWrapperFilterColumn, {
              [css.layoutWrapperFilterColumnEmbedded]: isEmbedded,
            })}
            data-testid="filterColumnAside"
          >
            <div
              className={classNames(css.filterColumnContent, {
                [css.filterColumnContentEmbedded]: isEmbedded,
              })}
            >
              {isEmbedded && sortConfig.active ? (
                <div className={css.embeddedSortBySection}>
                  <div className={css.embeddedSortByControl}>
                    {sortBy('desktop', {
                      isEmbeddedStyle: true,
                      contentPlacementOffset: 0,
                    })}
                  </div>
                </div>
              ) : null}
              {isEmbedded ? (
                <div className={css.embeddedKeywordSearchSection}>
                  <form
                    className={css.embeddedKeywordSearchForm}
                    onSubmit={this.handleEmbeddedKeywordSubmit}
                  >
                    <div className={css.embeddedKeywordInputWrapper}>
                      <input
                        className={css.embeddedKeywordInput}
                        type="text"
                        value={embeddedKeywordValue}
                        onChange={this.handleEmbeddedKeywordChange}
                        placeholder="Enter search term..."
                        autoComplete="off"
                      />
                      <span className={css.embeddedKeywordIcon}>
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M21 21L16.65 16.65M10.5 18C6.35786 18 3 14.6421 3 10.5C3 6.35786 6.35786 3 10.5 3C14.6421 3 18 6.35786 18 10.5C18 14.6421 14.6421 18 10.5 18Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </div>
                    <button type="submit" className={css.embeddedKeywordSearchButton}>
                      Search
                    </button>
                  </form>
                </div>
              ) : null}
              {filtersToRender.map(config => {
                return (
                  <FilterComponent
                    key={`SearchFiltersMobile.${config.scope || 'built-in'}.${config.key}`}
                    idPrefix="SearchFiltersMobile"
                    className={css.filter}
                    config={config}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={urlQueryParams}
                    initialValues={initialValues(this.props, this.state.currentQueryParams)}
                    getHandleChangedValueFn={this.getHandleChangedValueFn}
                    intl={intl}
                    liveEdit
                    showAsPopup={false}
                    isDesktop
                  />
                );
              })}
              <button className={css.resetAllButton} onClick={e => this.handleResetAll(e)}>
                <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
              </button>
            </div>
          </aside>
        )}

        <div
          className={classNames(css.layoutWrapperMain, {
            [css.layoutWrapperMainEmbedded]: isEmbedded,
          })}
          role="main"
        >
          <div
            className={classNames(css.searchResultContainer, {
              [css.searchResultContainerEmbedded]: isEmbedded,
            })}
          >
            {isEmbedded && embeddedTitle ? (
              <div className={css.embeddedResultsHeader}>
                <div className={css.embeddedResultsTitle}>{embeddedTitle}</div>
              </div>
            ) : null}
            {showChannelToggle ? (
              <ItemSourceToggle value={currentChannel} onChange={this.handleChannelChange} />
            ) : null}
            {showBrandCarousel ? (
              <BrandCarousel
                className={css.brandCarousel}
                title="Shop by brand"
                brands={this.props.brands}
                activeBrandName={currentBrand}
                onSelectBrand={this.handleBrandSelect}
                availableBrandValues={this.props.availableBrandValues}
              />
            ) : null}
            <SearchFiltersMobile
              className={css.searchFiltersMobileList}
              urlQueryParams={validQueryParams}
              sortByComponent={sortBy('mobile')}
              listingsAreLoaded={listingsAreLoaded}
              resultsCount={totalItems}
              searchInProgress={searchInProgress}
              searchListingsError={searchListingsError}
              showAsModalMaxWidth={MODAL_BREAKPOINT}
              onManageDisableScrolling={onManageDisableScrolling}
              onOpenModal={this.onOpenMobileModal}
              onCloseModal={this.onCloseMobileModal}
              resetAll={this.resetAll}
              selectedFiltersCount={selectedFiltersCountForMobile}
              isMapVariant={false}
              noResultsInfo={noResultsInfo}
            >
              {filtersToRender.map(config => {
                return (
                  <FilterComponent
                    key={`SearchFiltersMobile.${config.scope || 'built-in'}.${config.key}`}
                    idPrefix="SearchFiltersMobile"
                    config={config}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    initialValues={initialValues(this.props, this.state.currentQueryParams)}
                    getHandleChangedValueFn={this.getHandleChangedValueFn}
                    intl={intl}
                    liveEdit
                    showAsPopup={false}
                  />
                );
              })}
            </SearchFiltersMobile>
            {!isEmbedded ? (
              <div className={css.filterToggleRow}>
                <button
                  className={classNames(css.filterToggleButton, {
                    [css.filterToggleButtonActive]: showFilters,
                  })}
                  onClick={this.toggleFilters}
                >
                  {showFilters ? 'Close filters' : 'Open filters'}
                </button>
              </div>
            ) : null}
            <MainPanelHeader
              className={css.mainPanel}
              sortByComponent={isEmbedded ? null : sortBy('desktop')}
              isSortByActive={isEmbedded ? false : sortConfig.active}
              listingsAreLoaded={listingsAreLoaded}
              resultsCount={totalItems}
              searchInProgress={searchInProgress}
              searchListingsError={searchListingsError}
              noResultsInfo={noResultsInfo}
            />
            <div
              className={classNames(css.listingsForGridVariant, {
                [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
              })}
            >
              {searchListingsError ? (
                <H3 className={css.error}>
                  <FormattedMessage id="SearchPage.searchError" />
                </H3>
              ) : null}
              {!isValidDatesFilter ? (
                <H5>
                  <FormattedMessage id="SearchPage.invalidDatesFilter" />
                </H5>
              ) : null}
              <SearchResultsPanel
                className={css.searchListingsPanel}
                listings={listings}
                pagination={listingsAreLoaded ? pagination : null}
                search={parse(location.search)}
                isMapVariant={false}
                intl={intl}
                ListingCardComponent={this.props.ListingCardComponent}
                getListingCardProps={this.props.getListingCardProps}
                pageName={this.props.pageName || 'SearchPage'}
                pagePathParams={this.props.pagePathParams || {}}
                showToggleMap={this.props.showToggleMap}
              />
            </div>
          </div>
        </div>
      </div>
    );

    if (hideHeaderAndFooter) {
      return content;
    }

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer
          rootClassName={topbarClasses}
          currentPage="SearchPage"
          currentSearchParams={urlQueryParams}
        />
        {content}
        <FooterContainer />
      </Page>
    );
  }
}

SearchPageComponent.defaultProps = {
  listings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  showToggleMap: true,
  embeddedTitle: null,
  showSourceToggle: false,
  showBrandCarousel: false,
  brands: null,
  availableBrandValues: null,
};

SearchPageComponent.propTypes = {
  listings: array,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,
  embeddedTitle: string,
  showSourceToggle: bool,
  showBrandCarousel: bool,
  brands: array,
  availableBrandValues: arrayOf(string),

  // from useHistory
  history: shape({
    push: func.isRequired,
    replace: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const pageName = props.pageName || 'SearchPage';
  const pagePathParams = props.pagePathParams || {};

  useEffect(() => {
    const rawQueryParams = parse(location.search);
    const sanitizedQueryParams = sanitizeTaxonomyFilterParams(rawQueryParams);
    const rawQueryString = stringify(rawQueryParams);
    const sanitizedQueryString = stringify(sanitizedQueryParams);

    if (rawQueryString !== sanitizedQueryString) {
      history.replace(
        createResourceLocatorString(
          pageName,
          routeConfiguration,
          pagePathParams,
          sanitizedQueryParams
        )
      );
    }
  }, [history, location.search, pageName, pagePathParams, routeConfiguration]);

  useEffect(() => {
    let isMounted = true;

    const maybeRedirectToHostedStore = async () => {
      const { shouldRedirect } = await redirectToStore({
        redirect: false,
        history,
        routeConfiguration,
      });
      if (isMounted && shouldRedirect) {
        await redirectToStore({ redirect: true, history, routeConfiguration });
      }
    };

    maybeRedirectToHostedStore();

    return () => {
      isMounted = false;
    };
  }, [history, routeConfiguration]);

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    availableBrandValues,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    availableBrandValues,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedSearchPage);

export default SearchPage;
